/**
 * Supervisory Visits Tab Component for Client Detail Page
 * Displays supervisory visits with form uploads
 */
import { Button } from "@/components/ui/button";
import { Plus, Eye, Trash2, Calendar, File } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SupervisoryTab({
  supervisoryVisits,
  onAddVisit,
  onDeleteVisit,
}) {
  return (
    <div data-testid="client-supervisory-tab" className="space-y-8">
      {/* Supervisory Visits Section */}
      <div className="form-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              Supervisory Visits ({supervisoryVisits.length})
            </h3>
            <p className="text-sm text-slate-500">
              Track supervisory visits and upload visit forms
            </p>
          </div>
          <Button
            size="sm"
            onClick={onAddVisit}
            className="gap-2"
            data-testid="add-supervisory-btn"
          >
            <Plus className="w-4 h-4" />
            Record Visit
          </Button>
        </div>

        {supervisoryVisits.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No supervisory visits recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {supervisoryVisits.map((visit) => (
              <div
                key={visit.id}
                className="p-4 border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors"
                data-testid={`supervisory-visit-${visit.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        visit.visit_completed
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {visit.visit_completed ? "Completed" : "Pending"}
                      </span>
                      <span className="text-sm font-medium text-slate-800">
                        {visit.visit_date}
                      </span>
                    </div>
                    
                    <p className="text-sm text-slate-600">
                      Supervisor: {visit.supervisor_name}
                    </p>
                    
                    {visit.notes && (
                      <p className="text-sm text-slate-500 mt-1 italic">
                        {visit.notes}
                      </p>
                    )}
                    
                    {visit.next_visit_date && (
                      <p className="text-xs text-blue-600 mt-2">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Next visit scheduled: {visit.next_visit_date} {visit.next_visit_time && `at ${visit.next_visit_time}`}
                      </p>
                    )}
                    
                    {visit.form_file_name && (
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <File className="w-3 h-3" />
                        Form: {visit.form_file_name}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {visit.form_file_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => window.open(`${API}/documents/${visit.form_file_id}/view`, '_blank')}
                      >
                        <Eye className="w-3 h-3" />
                        View Form
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      onClick={() => onDeleteVisit(visit.id)}
                      data-testid={`delete-supervisory-${visit.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
